import { Download, FileWarning, Loader, Trash2 } from "lucide-react";
import { useState } from "react";
import { twMerge } from "tailwind-merge";

import { Button } from "@/components/button";
import { MediaPreviewModal } from "@/components/media-preview-modal";

import type { FileListProps, ManagedFile } from "../types";

import { useDownloadFile } from "../hooks/useDownloadFile";
import {
  convertManagedFilesToMediaItems,
  getFileExtension,
  getFileName,
} from "../utils";
import { DeleteFileModal } from "./DeleteFileModal";
import { FileIcon } from "./FileIcon";
import { ProgressSpinner } from "./ProgressSpinner";

export function FileList({ files, onRemove, onRetry }: FileListProps) {
  const [selectedFileId, setSelectedFileId] = useState<string | undefined>();

  if (files.length === 0) return null;

  const mediaItems = convertManagedFilesToMediaItems(files);

  const handlePreview = (fileId: string) => {
    const file = files.find((f) => f.id === fileId);

    if (file?.status === "success") {
      setSelectedFileId(fileId);
    }
  };

  return (
    <>
      <div className="flex flex-col gap-lg">
        {files.map((file) => (
          <FileItem
            file={file}
            key={file.id}
            onPreview={handlePreview}
            onRemove={onRemove}
            onRetry={onRetry}
          />
        ))}
      </div>

      {selectedFileId && mediaItems.length > 0 && (
        <MediaPreviewModal
          currentItemId={selectedFileId}
          mediaItems={mediaItems}
          onClose={() => setSelectedFileId(undefined)}
        />
      )}
    </>
  );
}

interface FileItemProps {
  file: ManagedFile;
  onPreview: (fileId: string) => void;
  onRemove: (fileId: string) => void;
  onRetry?: (fileId: string) => void;
}

function FileItem({ file, onPreview, onRemove, onRetry }: FileItemProps) {
  const { errorMessage, id, status } = file;
  const fileName = getFileName(file);
  const extension = getFileExtension(file);
  const canPreview = status === "success" && isPreviewable(extension);

  const handleClick = (e: React.MouseEvent) => {
    if (e.target instanceof HTMLElement && e.target.closest("button")) {
      return;
    }

    if (canPreview) {
      e.stopPropagation();
      onPreview(id);
    } else {
      e.stopPropagation();
    }
  };

  return (
    <div
      className={twMerge(
        "flex items-center justify-between gap-xs rounded-xl border border-border-secondary bg-bg-primary px-xl py-md",
        "hover:bg-bg-primary-hover hover:border-button-secondary-border-hover",
        canPreview && "cursor-pointer",
      )}
      onClick={handleClick}
    >
      <div className="flex items-center gap-md overflow-hidden">
        <FileIcon extension={extension} />
        <div className="min-w-0 flex-1">
          <span
            className="truncate text-sm font-medium text-text-secondary block"
            title={fileName}
          >
            {fileName}
          </span>
          {status === "error" && errorMessage && (
            <p className="truncate text-xs text-text-error-primary mt-xs">
              {errorMessage}
            </p>
          )}
        </div>
      </div>

      <div className="flex shrink-0 gap-xs">
        {status === "uploading" && <UploadingButtons file={file} />}
        {status === "pending" && <UploadingButtons file={file} />}
        {status === "success" && (
          <SuccessButtons file={file} onRemove={onRemove} />
        )}
        {status === "error" && (
          <ErrorButtons
            file={file}
            onRemove={onRemove}
            onRetry={file.isRetryable === false ? undefined : onRetry}
          />
        )}
      </div>
    </div>
  );
}

interface FileButtonsProps {
  file: ManagedFile;
  onRemove: (fileId: string) => void;
  onRetry?: (fileId: string) => void;
}

function UploadingButtons({ file }: { file: ManagedFile }) {
  if (file.status === "pending") {
    return (
      <Button color="gray" isIconOnly size="sm" variant="tertiary">
        <Loader className="animate-spin text-border-brand-solid" />
      </Button>
    );
  }

  // Show progress spinner for uploading status
  return (
    <Button color="gray" isIconOnly size="sm" variant="tertiary">
      <div className="size-4">
        <ProgressSpinner progress={file.progress} size={16} />
      </div>
    </Button>
  );
}

function SuccessButtons({ file, onRemove }: FileButtonsProps) {
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const { mutate: downloadFile } = useDownloadFile();
  const fileName = getFileName(file);

  return (
    <>
      <DeleteFileModal
        fileName={fileName}
        isOpen={isDeleteModalOpen}
        onDelete={() => {
          onRemove(file.id);
          setIsDeleteModalOpen(false);
        }}
        onOpenChange={setIsDeleteModalOpen}
      />
      <Button
        color="gray"
        isIconOnly
        onPress={() => setIsDeleteModalOpen(true)}
        size="sm"
        variant="tertiary"
      >
        <Trash2 className="text-button-tertiary-fg" />
      </Button>
      <Button
        color="gray"
        isIconOnly
        onPress={() => {
          if (file.file) {
            handleDownload(file.file);
          } else if (file.url) {
            window.open(file.url, "_blank");
          } else if (file.attachmentId) {
            downloadFile(file.attachmentId);
          }
        }}
        size="sm"
        variant="tertiary"
      >
        <Download className="text-button-tertiary-color-fg" />
      </Button>
    </>
  );
}

function ErrorButtons({ file, onRemove, onRetry }: FileButtonsProps) {
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const fileName = getFileName(file);

  return (
    <>
      <DeleteFileModal
        fileName={fileName}
        isOpen={isDeleteModalOpen}
        onDelete={() => {
          onRemove(file.id);
          setIsDeleteModalOpen(false);
        }}
        onOpenChange={setIsDeleteModalOpen}
      />
      <Button
        color="gray"
        isIconOnly
        onPress={() => setIsDeleteModalOpen(true)}
        size="sm"
        variant="tertiary"
      >
        <Trash2 className="text-button-tertiary-fg" />
      </Button>
      {onRetry ? (
        <Button
          color="gray"
          isIconOnly
          onPress={() => onRetry(file.id)}
          size="sm"
          variant="tertiary"
        >
          <FileWarning className="text-text-error-primary" />
        </Button>
      ) : (
        <Button color="gray" isDisabled isIconOnly size="sm" variant="tertiary">
          <FileWarning className="text-text-error-primary" />
        </Button>
      )}
    </>
  );
}

function handleDownload(file: File) {
  const url = URL.createObjectURL(file);
  const a = document.createElement("a");

  a.href = url;
  a.download = file.name;
  document.body.append(a);
  a.click();
  URL.revokeObjectURL(url);
  a.remove();
}

// File types that support preview in MediaViewer
const PREVIEWABLE_EXTENSIONS = new Set([
  // Images
  "png",
  "bmp",
  "webp",
  "jpg",
  "jpeg",
  "svg",
  "gif",
  // Videos
  "avi",
  "mov",
  "mkv",
  "mp4",
  "m4v",
  // PDF
  "pdf",
]);

function isPreviewable(extension: null | string): boolean {
  if (!extension) return false;

  return PREVIEWABLE_EXTENSIONS.has(extension.toLowerCase());
}
